import {
  decodeDatabaseBytesText,
  encodeDatabaseBytesText,
  isDatabaseBytesText,
} from '@streamlain/database-identity';

import { PublicationError } from '../publication';
import type {
  DatabaseCrdtValue,
  DatabaseJsonValue,
} from './database-writer-kernel';

function object(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}
const malformed = () => new PublicationError('FORMAT_MISMATCH');
function decodeBytes(text: string): Uint8Array {
  if (!isDatabaseBytesText(text)) throw malformed();
  return decodeDatabaseBytesText(text);
}

/** JSON of a CRDT value with bytes as base64: the WASM wire and value equality. */
export function stringifyDatabaseCrdt(value: unknown): string {
  return JSON.stringify(value, (_key, child: unknown) =>
    child instanceof Uint8Array ? encodeDatabaseBytesText(child) : child
  );
}

/**
 * One stored value equals another whatever the order of their keys: an object
 * that crossed the Rust codec comes back with its keys in any order. A key
 * holding undefined counts as absent and an array item holding it as null,
 * as in JSON.
 */
export function sameDatabaseJsonValue(a: unknown, b: unknown): boolean {
  if (a instanceof Uint8Array || b instanceof Uint8Array)
    return stringifyDatabaseCrdt(a) === stringifyDatabaseCrdt(b);
  if (Array.isArray(a) || Array.isArray(b))
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) =>
        sameDatabaseJsonValue(item ?? null, b[index] ?? null)
      )
    );
  if (isJsonRecord(a) && isJsonRecord(b)) {
    const keys = definedKeys(a);
    return (
      keys.length === definedKeys(b).length &&
      keys.every(key => sameDatabaseJsonValue(a[key], b[key]))
    );
  }
  return a === b;
}
function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}
function definedKeys(value: Record<string, unknown>): string[] {
  return Object.keys(value).filter(key => value[key] !== undefined);
}

export function databaseJsonValue(value: unknown): DatabaseJsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean')
    return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return value.map(databaseJsonValue);
  if (object(value)) {
    const result: Record<string, DatabaseJsonValue> = Object.create(null);
    for (const [key, child] of Object.entries(value))
      result[key] = databaseJsonValue(child);
    // Return a plain record without assigning untrusted keys through setters.
    return Object.fromEntries(Object.entries(result));
  }
  throw malformed();
}

export function databaseCrdtValue(value: unknown): DatabaseCrdtValue {
  if (!object(value)) throw malformed();
  switch (value.kind) {
    case 'absent':
      return { kind: 'absent' };
    case 'value':
      return { kind: 'value', value: databaseJsonValue(value.value) };
    case 'bytes':
      if (!(value.value instanceof Uint8Array)) throw malformed();
      return { kind: 'bytes', value: value.value.slice() };
    case 'map': {
      if (!Array.isArray(value.entries)) throw malformed();
      const names = new Set<string>();
      const entries: [string, DatabaseCrdtValue][] = value.entries.map(
        entry => {
          if (
            !Array.isArray(entry) ||
            entry.length !== 2 ||
            typeof entry[0] !== 'string' ||
            names.has(entry[0])
          )
            throw malformed();
          names.add(entry[0]);
          return [entry[0], databaseCrdtValue(entry[1])];
        }
      );
      return { kind: 'map', entries };
    }
    case 'array': {
      if (!Array.isArray(value.items)) throw malformed();
      return { kind: 'array', items: value.items.map(databaseCrdtValue) };
    }
    case 'text': {
      if (!Array.isArray(value.delta)) throw malformed();
      return {
        kind: 'text',
        delta: value.delta.map(run => {
          if (!object(run) || typeof run.insert !== 'string') throw malformed();
          if (run.attributes === undefined) return { insert: run.insert };
          if (!object(run.attributes)) throw malformed();
          const attributes = Object.fromEntries(
            Object.entries(run.attributes).map(([key, child]) => [
              key,
              databaseJsonValue(child),
            ])
          );
          return { insert: run.insert, attributes };
        }),
      };
    }
    default:
      throw malformed();
  }
}

/** JSON parsed here is detached and privately owned. Validate without allocating
 * a second full tree, decoding base64 bytes in place; caller-supplied values
 * still use the cloning converters. */
export function parseDatabaseCrdtValue(json: string): DatabaseCrdtValue {
  const value: unknown = JSON.parse(json);
  if (!validCrdt(value)) throw malformed();
  return value;
}
function validJson(value: unknown): value is DatabaseJsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean')
    return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(validJson);
  return object(value) && Object.values(value).every(validJson);
}
function validCrdt(value: unknown): value is DatabaseCrdtValue {
  if (!object(value)) return false;
  switch (value.kind) {
    case 'absent':
      return true;
    case 'value':
      return validJson(value.value);
    case 'bytes':
      if (typeof value.value !== 'string') return false;
      value.value = decodeBytes(value.value);
      return true;
    case 'array':
      return Array.isArray(value.items) && value.items.every(validCrdt);
    case 'map': {
      if (!Array.isArray(value.entries)) return false;
      const names = new Set<string>();
      return value.entries.every(entry => {
        if (
          !Array.isArray(entry) ||
          entry.length !== 2 ||
          typeof entry[0] !== 'string' ||
          names.has(entry[0]) ||
          !validCrdt(entry[1])
        )
          return false;
        names.add(entry[0]);
        return true;
      });
    }
    case 'text':
      return (
        Array.isArray(value.delta) &&
        value.delta.every(
          run =>
            object(run) &&
            typeof run.insert === 'string' &&
            (run.attributes === undefined ||
              (object(run.attributes) &&
                Object.values(run.attributes).every(validJson)))
        )
      );
    default:
      return false;
  }
}
